import React, { useState } from 'react';
import './DataUploadView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type UploadType = 'radio' | 'tv' | 'display' | 'dooh' | 'ott_ctv' | 'youtube' | 'search' | 'sport_signage' | 'mlb_signage' | 'mlb_social' | 'fta';

interface ValidationResult {
  records_queryable: number;
  matched_dealers: number;
  unmatched_zips: string[];
  date_range: string | null;
  geographic_coverage: {
    total_zips: number;
    zips_with_dealers: number;
    zips_without_dealers: number;
    coverage_percentage: number;
  };
  sample_query: {
    dealer_count: number;
    total_impressions: number;
    records_count: number;
  };
  warnings: string[];
  status: 'success' | 'partial' | 'failed';
}

interface UploadResult {
  success: boolean;
  message: string;
  records?: number;
  validation?: ValidationResult;
}

const DataUploadView: React.FC = () => {
  const [uploadType, setUploadType] = useState<UploadType>('display');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    setResult(null); // Clear previous results
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/upload/${uploadType}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const data = await response.json();

      if (response.status === 401) {
        // Session expired — token was invalidated (e.g. server redeployed)
        setResult({
          success: false,
          message: 'Session expired — please log out and log back in, then retry the upload.'
        });
      } else if (response.ok && data.success) {
        setResult({
          success: true,
          message: data.message || `Successfully uploaded ${data.records} records`,
          records: data.records,
          validation: data.validation
        });
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setResult({
          success: false,
          message: data.error || 'Upload failed'
        });
      }
    } catch (error: any) {
      setResult({
        success: false,
        message: `Upload failed: ${error.message}`
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = (type: UploadType) => {
    const link = document.createElement('a');
    link.href = `${API_BASE_URL}/api/admin/data/sample_${type}.csv`;
    link.download = `sample_${type}.csv`;
    link.click();
  };

  const csvFormats: Record<UploadType, {
    icon: string;
    label: string;
    columns: string[];
    example: string;
    notes: string[];
  }> = {
    display: {
      icon: 'display_settings',
      label: 'Display Advertising',
      columns: ['advertiser', 'campaign', 'zip', 'date', 'impressions', 'clicks', 'view_throughs'],
      example: `Gravely,Summer Display Campaign,10001,2024-08-01,50000,150,25
Gravely,Summer Display Campaign,10002,2024-08-01,48000,145,23`,
      notes: [
        'ZIP-based geographic attribution',
        'Date format: YYYY-MM-DD',
        'Optional columns: conversions, offline_visits, product',
        'Impressions, clicks, view_throughs should be integers'
      ]
    },
    dooh: {
      icon: 'signpost',
      label: 'DOOH (Digital Out-of-Home)',
      columns: ['date', 'zip', 'impressions'],
      example: `2024-08-01,10001,2500
2024-08-01,10002,3200
2024-08-02,10003,2800`,
      notes: [
        'ZIP-based geographic attribution',
        'Date format: YYYY-MM-DD',
        'Impressions should be integers',
        'Simple format: just date, ZIP code, and impressions'
      ]
    },
    ott_ctv: {
      icon: 'connected_tv',
      label: 'OTT/CTV (Streaming TV)',
      columns: ['advertiser', 'campaign', 'zip', 'date', 'impressions', 'clicks', 'view_throughs'],
      example: `Gravely,Streaming TV Campaign,10001,2024-08-01,5000,15,2
Gravely,Streaming TV Campaign,10002,2024-08-01,4500,12,1`,
      notes: [
        'ZIP-based geographic attribution',
        'Date format: YYYY-MM-DD',
        'Optional columns: conversions, offline_visits, product',
        'Lower impression counts typical for OTT/CTV'
      ]
    },
    youtube: {
      icon: 'play_circle',
      label: 'YouTube Advertising',
      columns: ['advertiser', 'campaign', 'zip', 'date', 'impressions', 'clicks', 'view_throughs'],
      example: `Gravely,YouTube Video Campaign,10001,2024-08-01,15000,450,30
Gravely,YouTube Video Campaign,10002,2024-08-01,12000,380,25`,
      notes: [
        'ZIP-based geographic attribution',
        'Date format: YYYY-MM-DD',
        'Optional columns: conversions, offline_visits, product',
        'View-throughs are important for video advertising'
      ]
    },
    search: {
      icon: 'search',
      label: 'Search/SEM',
      columns: ['City', 'Region', 'Sessions'],
      example: `Atlanta,Southeast,1250
Charlotte,Southeast,890`,
      notes: [
        'City-based (NOT ZIP-based)',
        'Sessions represent user engagement',
        'Geographic validation handled differently',
        'Region should match dealer regions'
      ]
    },
    radio: {
      icon: 'radio',
      label: 'Radio Spots',
      columns: ['start_date', 'end_date', 'station', 'impressions', 'dma', 'dma_code'],
      example: `11/17/2025,11/30/2025,WCHZ,16000,Augusta,520
11/17/2025,11/30/2025,WGAC-AM,42000,Augusta,520
11/17/2025,11/30/2025,WROR-FM,240000,Boston (Manchester),506`,
      notes: [
        'NEW FORMAT: Date ranges instead of single dates',
        'Impressions are distributed evenly across the date range',
        'Date format: MM/DD/YYYY (e.g., 11/17/2025)',
        'DMA-based geographic attribution',
        'Each CSV row creates multiple daily records'
      ]
    },
    tv: {
      icon: 'tv',
      label: 'TV Spots',
      columns: ['ZONE', 'START_DATE', 'END_DATE', 'NETWORK', 'PROGRAMMING', 'IMPRESSIONS', 'DMA_CODE'],
      example: `Atlanta Interconnect,01/06/2025,01/12/2025,NFLN,Minnesota Vikings at Cleveland Browns,"52,345",524
Houston Interconnect,01/06/2025,01/12/2025,NFLN,Minnesota Vikings at Cleveland Browns,"18,200",618
Boston (Manchester) Interconnect,01/06/2025,01/12/2025,NFLN,Minnesota Vikings at Cleveland Browns,"9,100",`,
      notes: [
        'Date ranges: START_DATE + END_DATE — impressions distributed evenly across days',
        'Date format: MM/DD/YYYY (e.g., 01/06/2025)',
        'DMA_CODE column is optional — when provided, DMA is matched directly (most reliable)',
        'When DMA_CODE is blank, the ZONE name is used to look up the correct DMA automatically',
        'Zone names accepted in any format: "Atlanta Interconnect", "Houston, TX Interconnect", "Comcast/Boston Interconnect"',
        'Impressions may contain commas (will be cleaned)',
        'Legacy single-DATE format still accepted for historical uploads'
      ]
    },
    fta: {
      icon: 'store',
      label: 'Store Visits (FTA)',
      columns: ['Conversion ID', 'Conversion Time', 'TD2', 'Conversion Device Type', 'Last Impression Campaign Name', 'Impression Count'],
      example: `(Raw TDI export — upload the file as-is, no reformatting needed)`,
      notes: [
        'Upload the raw TDI CSV export directly — no reformatting required',
        'Rows with empty TD2 (website conversions) are automatically skipped',
        'Each TD2 value is matched to a dealer via address + name matching',
        'Safe to re-upload the same file — duplicates are automatically skipped',
        'Unmatched dealers are listed in the upload result for review'
      ]
    },
    mlb_social: {
      icon: 'thumb_up',
      label: 'MLB National Social',
      columns: ['date', 'likes', 'comments', 'shares', 'views'],
      example: `3/28/2026,1398,26,56,140952
3/29/2026,-,-,40,3
3/30/2026,1338,17,85,64191`,
      notes: [
        'National social media data — aggregated across all MLB content',
        'Multiple rows per date are summed together automatically',
        'Dashes (-) are treated as 0',
        'Date format: M/D/YYYY or YYYY-MM-DD',
        'Re-uploading the same dates updates existing records in-place'
      ]
    },
    mlb_signage: {
      icon: 'stadium',
      label: 'MLB Signage',
      columns: ['start_date', 'end_date', 'location', 'team', 'impressions', 'dma_code'],
      example: `3/26/2026,4/5/2026,"Albany, GA",Atlanta Braves,25258,525
4/6/2026,4/19/2026,"Albany, GA",Atlanta Braves,31400,525`,
      notes: [
        'Aggregated MLB signage impressions by team and DMA market',
        'start_date / end_date define the impression window',
        'Impressions are attributed to the start_date month for chart bucketing',
        'Date format: M/D/YYYY or YYYY-MM-DD',
        'Re-uploading the same rows updates existing records in-place (no duplicates)'
      ]
    },
    sport_signage: {
      icon: 'sports_basketball',
      label: 'Sport Signage',
      columns: ['date', 'time_et', 'away_team', 'home_team', 'conference', 'tv_network', 'impressions', 'home_dma_code', 'home_dma_name', 'away_dma_code', 'away_dma_name'],
      example: `2/11/2026,6:00 PM,Iowa,Maryland,Big Ten,FS1,52345,511,Washington DC (Hagerstown),679,Des Moines-Ames
2/12/2026,7:00 PM,Delaware,FIU,C-USA,ESPN+,18500,528,Miami-Ft. Lauderdale,504,Philadelphia`,
      notes: [
        'DMA-based geographic attribution (home AND away market)',
        'Date format: M/D/YYYY (e.g., 2/11/2026)',
        'Dealers in both home and away DMA markets will see each game',
        'Include both home_dma_code and away_dma_code for dual-market coverage',
        'One row per game'
      ]
    }
  };

  const currentFormat = csvFormats[uploadType];

  const channelButtons: { type: UploadType; category: string }[] = [
    { type: 'display', category: 'digital' },
    { type: 'dooh', category: 'digital' },
    { type: 'ott_ctv', category: 'digital' },
    { type: 'youtube', category: 'digital' },
    { type: 'search', category: 'digital' },
    { type: 'radio', category: 'broadcast' },
    { type: 'tv', category: 'broadcast' },
    { type: 'sport_signage', category: 'broadcast' },
    { type: 'mlb_signage', category: 'broadcast' },
    { type: 'mlb_social', category: 'broadcast' },
    { type: 'fta', category: 'attribution' }
  ];

  return (
    <div className="data-upload-view">
      <div className="upload-header">
        <h2>Upload Advertising Data</h2>
        <p>Import advertising data from CSV files across all channels</p>
      </div>

      <div className="upload-type-selector">
        <div className="channel-category">
          <h4>Digital Channels</h4>
          <div className="channel-buttons">
            {channelButtons.filter(c => c.category === 'digital').map(({ type }) => (
              <button
                key={type}
                onClick={() => {
                  setUploadType(type);
                  setFile(null);
                  setResult(null);
                }}
                className={`type-button ${uploadType === type ? 'active' : ''}`}
              >
                <i className="material-icons">{csvFormats[type].icon}</i>
                <span>{csvFormats[type].label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="channel-category">
          <h4>Broadcast Channels</h4>
          <div className="channel-buttons">
            {channelButtons.filter(c => c.category === 'broadcast').map(({ type }) => (
              <button
                key={type}
                onClick={() => {
                  setUploadType(type);
                  setFile(null);
                  setResult(null);
                }}
                className={`type-button ${uploadType === type ? 'active' : ''}`}
              >
                <i className="material-icons">{csvFormats[type].icon}</i>
                <span>{csvFormats[type].label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="channel-category">
          <h4>Attribution</h4>
          <div className="channel-buttons">
            {channelButtons.filter(c => c.category === 'attribution').map(({ type }) => (
              <button
                key={type}
                onClick={() => {
                  setUploadType(type);
                  setFile(null);
                  setResult(null);
                }}
                className={`type-button ${uploadType === type ? 'active' : ''}`}
              >
                <i className="material-icons">{csvFormats[type].icon}</i>
                <span>{csvFormats[type].label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="upload-card">
        <div className="file-input-section">
          <label htmlFor="file-input" className="file-label">
            <i className="material-icons">upload_file</i>
            <span>{file ? file.name : 'Choose CSV file'}</span>
          </label>
          <input
            id="file-input"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="file-input"
          />
        </div>

        {file && (
          <div className="file-info">
            <i className="material-icons">description</i>
            <div className="file-details">
              <span className="file-name">{file.name}</span>
              <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
            </div>
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="upload-button"
        >
          {uploading ? (
            <>
              <span className="spinner"></span>
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <i className="material-icons">cloud_upload</i>
              <span>Upload Data</span>
            </>
          )}
        </button>

        {result && (
          <div className={`result-section ${result.success ? 'success' : 'error'}`}>
            <div className="result-message">
              <i className="material-icons">
                {result.success ? 'check_circle' : 'error'}
              </i>
              <span>{result.message}</span>
            </div>

            {result.validation && (
              <div className="validation-results">
                <h4>Upload Validation Results</h4>

                <div className="validation-stats">
                  <div className="stat-card">
                    <i className="material-icons">database</i>
                    <div>
                      <span className="stat-value">{result.validation.records_queryable}</span>
                      <span className="stat-label">Records Uploaded</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <i className="material-icons">store</i>
                    <div>
                      <span className="stat-value">{result.validation.matched_dealers}</span>
                      <span className="stat-label">Dealers Matched</span>
                    </div>
                  </div>

                  {result.validation.geographic_coverage && (
                    <div className="stat-card">
                      <i className="material-icons">map</i>
                      <div>
                        <span className="stat-value">
                          {result.validation.geographic_coverage.coverage_percentage}%
                        </span>
                        <span className="stat-label">Geographic Coverage</span>
                      </div>
                    </div>
                  )}

                  {result.validation.sample_query && (
                    <div className="stat-card">
                      <i className="material-icons">visibility</i>
                      <div>
                        <span className="stat-value">
                          {result.validation.sample_query.total_impressions.toLocaleString()}
                        </span>
                        <span className="stat-label">Total Impressions</span>
                      </div>
                    </div>
                  )}
                </div>

                {result.validation.date_range && (
                  <div className="validation-detail">
                    <i className="material-icons">date_range</i>
                    <span>Date Range: {result.validation.date_range}</span>
                  </div>
                )}

                {result.validation.warnings && result.validation.warnings.length > 0 && (
                  <div className="validation-warnings">
                    <h5>
                      <i className="material-icons">warning</i>
                      Warnings
                    </h5>
                    <ul>
                      {result.validation.warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className={`validation-status ${result.validation.status}`}>
                  <i className="material-icons">
                    {result.validation.status === 'success' ? 'check_circle' :
                     result.validation.status === 'partial' ? 'warning' : 'error'}
                  </i>
                  <span>Status: {result.validation.status.toUpperCase()}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="format-guide">
        <div className="format-header">
          <h3>
            <i className="material-icons">info</i>
            CSV Format Requirements
          </h3>
          <button
            onClick={() => downloadTemplate(uploadType)}
            className="download-template-button"
          >
            <i className="material-icons">download</i>
            Download Template
          </button>
        </div>

        <div className="format-details">
          <h4>Required Columns:</h4>
          <div className="column-list">
            {currentFormat.columns.map((col, idx) => (
              <span key={idx} className="column-badge">{col}</span>
            ))}
          </div>

          <h4>Example CSV Content:</h4>
          <pre className="csv-example">
            <code>
              {currentFormat.columns.join(',')}{'\n'}
              {currentFormat.example}
            </code>
          </pre>

          <div className="format-notes">
            <h4>Important Notes:</h4>
            <ul>
              {currentFormat.notes.map((note, idx) => (
                <li key={idx}>{note}</li>
              ))}
              <li>File must be in CSV format (.csv extension)</li>
              <li>First row must contain column headers</li>
              <li>Maximum file size: 100,000 rows</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataUploadView;
